/**
 * The Machine Board (#492) as the room mounts it.
 *
 * Everything the board shows is already on the desk, handed in as rows. The one
 * extra read is this machine's `live_terminals`, through the reader Settings
 * already uses for the same list, so a terminal no Worker owns is not missed.
 * Until that read lands the board says its terminal list is incomplete rather
 * than empty. Nothing here writes.
 */
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import type { MachineCapacity } from '@brunel/shared';
import type { AiCapacityMeter } from '../lib/ai-usage-view';
import type {
  AiConnectionStatusRow, AiLocalRuntimeStatusRow, LaunchCatalogRow, ModelOfferingRef, NodeOffering,
  NodePresence, Reader, RunningTask, RuntimeOutage, TerminalSessionRow, WorkerOfferingPreference,
  WorkerRoutingRow, WorkerTerminalRow, WorkforceSession,
} from '../lib/data';
import type { AiConnectionRoleBindingRow } from '../lib/ai-role-bindings';
import { buildMachineBoard, machineBoardHash, parseMachineBoardHash } from '../lib/machine-board';
import { MachineBoard } from './MachineBoard';

/** How often the board re-reads this machine's terminals while its tab is visible. */
const TERMINALS_REFRESH_MS = 10_000;

export interface MachineBoardRows {
  readonly nodes: readonly NodePresence[];
  readonly workerTerminals: readonly WorkerTerminalRow[];
  readonly workforceSessions: readonly WorkforceSession[];
  readonly nodeOfferings: readonly NodeOffering[];
  readonly modelOfferings: readonly ModelOfferingRef[];
  readonly workerOfferingPreferences: readonly WorkerOfferingPreference[];
  readonly aiConnectionStatuses: readonly AiConnectionStatusRow[];
  readonly aiLocalRuntimeStatuses: readonly AiLocalRuntimeStatusRow[];
  readonly aiConnectionRoleBindings: readonly AiConnectionRoleBindingRow[];
  readonly running: readonly RunningTask[];
  readonly workerRouting: readonly WorkerRoutingRow[];
  readonly outages: readonly RuntimeOutage[];
  readonly launchCatalog: readonly LaunchCatalogRow[];
}

export interface MachineBoardPageProps {
  readonly reader: Reader;
  readonly orgId: string;
  readonly nodeId: string;
  readonly nowMs: number;
  readonly rows: MachineBoardRows;
  /** The one #483 capacity model the rest of the screen reads. */
  readonly machines: readonly MachineCapacity[];
  readonly meters: readonly AiCapacityMeter[];
  readonly pendingApprovalsByWorker: ReadonlyMap<string, number>;
  readonly onClose: () => void;
  readonly onSwitchMachine: (nodeId: string) => void;
  readonly onOpenWorker: (workerId: string) => void;
  readonly onOpenTask: (taskId: string) => void;
  readonly onOpenUsage: (meterKey: string) => void;
  readonly onOpenCapacity: (nodeId: string) => void;
}

export function MachineBoardPage({
  reader, orgId, nodeId, nowMs, rows, machines, meters, pendingApprovalsByWorker, ...actions
}: MachineBoardPageProps): ReactElement {
  const [terminals, setTerminals] = useState<{ nodeId: string; rows: readonly TerminalSessionRow[] } | null>(null);

  useEffect(() => {
    let live = true;
    let inFlight = false;
    let timer: number | undefined;
    const read = async () => {
      if (!live || document.hidden || inFlight) return;
      inFlight = true;
      try {
        const result = await reader.terminalSessions(orgId, nodeId);
        if (live && !document.hidden && result.ok) setTerminals({ nodeId, rows: result.rows });
      } finally {
        inFlight = false;
      }
    };
    const onVisibility = () => {
      if (timer !== undefined) window.clearInterval(timer);
      if (document.hidden) return;
      void read();
      timer = window.setInterval(() => void read(), TERMINALS_REFRESH_MS);
    };
    document.addEventListener('visibilitychange', onVisibility);
    onVisibility();
    return () => {
      live = false;
      document.removeEventListener('visibilitychange', onVisibility);
      if (timer !== undefined) window.clearInterval(timer);
    };
  }, [reader, orgId, nodeId]);

  const runtimeLabels = useMemo(() => Object.fromEntries(rows.launchCatalog
    .filter((row) => row.runtime_label !== null && row.runtime_label.trim() !== '')
    .map((row) => [row.runtime_key, row.runtime_label as string])), [rows.launchCatalog]);

  const model = useMemo(() => buildMachineBoard({
    nodeId,
    nowMs,
    machine: machines.find((machine) => machine.nodeId === nodeId) ?? null,
    node: rows.nodes.find((node) => node.node_id === nodeId) ?? null,
    workerTerminals: rows.workerTerminals,
    workforceSessions: rows.workforceSessions,
    liveTerminals: terminals !== null && terminals.nodeId === nodeId ? terminals.rows : null,
    nodeOfferings: rows.nodeOfferings,
    modelOfferings: rows.modelOfferings,
    workerOfferingPreferences: rows.workerOfferingPreferences,
    connectionStatuses: rows.aiConnectionStatuses,
    localRuntimes: rows.aiLocalRuntimeStatuses,
    roleBindings: rows.aiConnectionRoleBindings,
    running: rows.running,
    workerRouting: rows.workerRouting,
    outages: rows.outages,
    meters,
    pendingApprovalsByWorker,
    runtimeLabels,
  }), [nodeId, nowMs, machines, rows, terminals, meters, pendingApprovalsByWorker, runtimeLabels]);

  const machineRefs = useMemo(() => machines.map((machine) => ({
    nodeId: machine.nodeId, name: machine.name, presence: machine.presence,
  })), [machines]);

  // Keyed by machine so switching boards starts on the default view with no stale filters.
  return <MachineBoard key={nodeId} model={model} machines={machineRefs} {...actions} />;
}

/**
 * Which machine's board is open, kept in the address as `#machine/<node>` so a
 * board can be bookmarked or linked to directly. Opening sets it, closing clears
 * it, and the browser's own back button closes the board.
 */
export function useMachineBoardLink(): {
  readonly nodeId: string | null;
  readonly open: (nodeId: string) => void;
  readonly close: () => void;
} {
  const read = () => (typeof window === 'undefined' ? null : parseMachineBoardHash(window.location.hash));
  const [nodeId, setNodeId] = useState<string | null>(read);
  useEffect(() => {
    const onHash = () => setNodeId(read());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  const open = useCallback((next: string) => {
    setNodeId(next);
    if (window.location.hash !== machineBoardHash(next)) window.location.hash = machineBoardHash(next);
  }, []);
  const close = useCallback(() => {
    setNodeId(null);
    if (parseMachineBoardHash(window.location.hash) !== null) {
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    }
  }, []);
  return { nodeId, open, close };
}
